import type { TranslationKey } from "@/config/translationKeys";
import type { Intervention } from "./types";

/**
 * Client-side role rules. These only decide what the UI offers; the
 * authoritative checks are the Row Level Security policies and RPC functions
 * in `supabase/*.sql`.
 */
export const ROLES = [
  "superadmin",
  "localadmin",
  "local_reader",
  "respondent",
  "respondent_read",
] as const;
export type Role = (typeof ROLES)[number];

/** Display names of the roles (header and user administration). */
export const ROLE_LABEL_KEYS = {
  localadmin: "adminInviteRoleLocalAdmin",
  local_reader: "adminInviteRoleLocalReader",
  respondent: "adminInviteRoleRespondent",
  respondent_read: "adminInviteRoleRespondentRead",
  superadmin: "adminInviteRoleSuperAdmin",
} as const satisfies Record<Role, TranslationKey>;

export type AuthMode = "shared" | "individual";

export interface CurrentUser {
  id: string;
  email: string;
  role: Role;
  /** Assigned location name; `null` for superadmins. */
  location: string | null;
}

export function parseRole(value: unknown): Role | null {
  return (ROLES as readonly unknown[]).includes(value) ? (value as Role) : null;
}

/** Respondents only see their own interventions in the list. */
export function isRespondentOnly(user: CurrentUser | null): boolean {
  return user?.role === "respondent";
}

/** Roles that report their own interventions through the questionnaire. */
export function canSubmitOwn(user: CurrentUser | null): boolean {
  return user?.role === "respondent" || user?.role === "respondent_read";
}

/** Readers (and the signed-out shared view) start with view F's non-prevention items hidden. */
export function showsAllFiltersByDefault(user: CurrentUser | null): boolean {
  return !!user && user.role !== "local_reader";
}

export function isSuperadmin(user: CurrentUser | null): boolean {
  return user?.role === "superadmin";
}

function isSameLocation(user: CurrentUser, item: Intervention): boolean {
  const itemLocation = item.region.trim();
  const userLocation = (user.location ?? "").trim();
  return !!itemLocation && !!userLocation && itemLocation === userLocation;
}

export function canCreateIntervention(user: CurrentUser | null): boolean {
  return !!user && (user.role === "superadmin" || user.role === "localadmin" || canSubmitOwn(user));
}

/** A workbook preview has no database UUID and must remain read-only. */
export function isPersistedIntervention(item: Intervention): boolean {
  return !!item.dbId;
}

export function canEditIntervention(user: CurrentUser | null, item: Intervention): boolean {
  if (!user || !isPersistedIntervention(item)) return false;
  if (user.role === "superadmin") return true;
  if (user.role === "localadmin") return isSameLocation(user, item);
  return (
    canSubmitOwn(user) &&
    item.status === "draft" &&
    !!item.submittedBy &&
    item.submittedBy === user.id
  );
}

/**
 * Mirrors `can_read_intervention_response`: superadmins, local admins for
 * their location, and respondents for their own interventions in any status
 * (they may only edit drafts, but can always see what they submitted).
 */
export function canReadPrivateResponse(user: CurrentUser | null, item: Intervention): boolean {
  if (!user || !isPersistedIntervention(item)) return false;
  if (user.role === "superadmin") return true;
  if (user.role === "localadmin") return isSameLocation(user, item);
  return canSubmitOwn(user) && !!item.submittedBy && item.submittedBy === user.id;
}

export function canDeleteIntervention(user: CurrentUser | null, item: Intervention): boolean {
  if (!user || !isPersistedIntervention(item)) return false;
  if (user.role === "superadmin") return true;
  return (
    canSubmitOwn(user) &&
    item.submittedBy === user.id &&
    (item.status === "draft" || item.status === "submitted")
  );
}

export function canApproveIntervention(user: CurrentUser | null, item: Intervention): boolean {
  if (!user || !isPersistedIntervention(item)) return false;
  return user.role === "superadmin" || (user.role === "localadmin" && isSameLocation(user, item));
}

/** Revert a submitted or published intervention to draft, including imported
 * interventions without a respondent. RLS still enforces write permissions. */
export function canReturnToDraft(user: CurrentUser | null, item: Intervention): boolean {
  return (
    canApproveIntervention(user, item) &&
    (item.status === "submitted" || item.status === "approved")
  );
}

/** Superadmins and local admins see the classification selects in the editor. */
export function canClassify(user: CurrentUser | null): boolean {
  return user?.role === "superadmin" || user?.role === "localadmin";
}

/**
 * Admin tab: superadmins get every section; local admins get users, GDPR and
 * export, limited to their own location (enforced by the admin API and RLS).
 */
export function canAccessAdmin(user: CurrentUser | null): boolean {
  return user?.role === "superadmin" || user?.role === "localadmin";
}

/** Roles a local admin may assign; mirrors `functions/api/admin/users.js`. */
const LOCAL_ADMIN_MANAGED_ROLES: readonly Role[] = [
  "local_reader",
  "respondent",
  "respondent_read",
];

/** Roles `user` may assign when inviting or editing users. */
export function getAssignableRoles(user: CurrentUser | null): readonly Role[] {
  if (user?.role === "superadmin") return ROLES;
  if (user?.role === "localadmin") return LOCAL_ADMIN_MANAGED_ROLES;
  return [];
}

/** Whether `user` may edit or delete `target` (never themselves). */
export function canManageUser(
  user: CurrentUser | null,
  target: { id: string; role: Role | null; location: string | null },
): boolean {
  if (!user || target.id === user.id) return false;
  if (user.role === "superadmin") return true;
  return (
    user.role === "localadmin" &&
    !!user.location &&
    target.location === user.location &&
    !!target.role &&
    LOCAL_ADMIN_MANAGED_ROLES.includes(target.role)
  );
}

export function canCreateSystemAnalysis(user: CurrentUser | null): boolean {
  return user?.role === "superadmin" || user?.role === "localadmin";
}
